import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Inbox, Mail, MessageSquare, Send } from "lucide-react";
import { errorMessage } from "@web/lib/api";
import { fmtStamp } from "@web/lib/format";
import { automationSettingsQuery, queueApi, queueQuery, type QueueItem } from "@web/lib/queries-automation";
import {
  BottomSheet, Button, Card, EmptyState, Inline, Page, Picker, Region,
  ResponsiveFilters, SearchBar, Skeleton, Stack, StatusChip, Text, TextArea,
  TextField, UnsavedChanges, Workspace, useToast,
} from "@web/ui";

/** Queue (SPEC §6.1 item 2): everything the automation has composed and nobody
 * has sent yet. Grouped by kind, because releasing six balance reminders is one
 * decision and releasing six sales nudges is a different one. */
const KIND_LABELS: Record<string, string> = {
  drip: "Sales sequence", payment: "Payments", reminder: "Reminders", damage: "Damage deposit",
  insurance: "Insurance", contract: "Agreements", tour: "Tours", system: "System", manual: "Manual",
};
const kindLabel = (kind: string) => KIND_LABELS[kind] ?? kind.replaceAll("_", " ");
const who = (item: QueueItem) => item.contactName ?? item.toAddr ?? "an unknown recipient";

/** What a release actually did. Quiet hours and the daily text cap survive the
 * release, so a message let go at ten at night is scheduled rather than sent —
 * and "Message sent" would be a lie. A failure the provider reported is not a
 * send either. */
function releaseOutcome(result: { released: number; failed: number; deferred: number | unknown[] }): string {
  const deferred = Array.isArray(result.deferred) ? result.deferred.length : result.deferred;
  const parts: string[] = [];
  if (result.released) parts.push(`${result.released} sent`);
  if (deferred) parts.push(`${deferred} waiting for the morning`);
  if (result.failed) parts.push(`${result.failed} could not be sent`);
  if (!parts.length) return "Nothing to send";
  return parts.join(" · ");
}

export type QueueSearch = { kind?: string; channel?: "sms" | "email"; q?: string };

export function Queue() {
  const navigate = useNavigate();
  const toast = useToast();
  // Filters live in the URL, so a refresh, a back button or a link shared with a
  // colleague all show the same queue (docs/plans/RESPONSIVE-STANDARD.md).
  const search = useSearch({ from: "/app/more/queue" }) as QueueSearch;
  const kind = search.kind ?? "", channel = search.channel ?? "", query = search.q ?? "";
  const setSearch = (next: QueueSearch) => navigate({ to: "/more/queue", search: { ...search, ...next }, replace: true });
  const [editing, setEditing] = useState<QueueItem | null>(null);
  const [cancelling, setCancelling] = useState<QueueItem | null>(null);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [busy, setBusy] = useState(false);

  const filters = { ...(kind ? { kind } : {}), ...(channel ? { channel: channel as "sms" | "email" } : {}), ...(query ? { q: query } : {}) };
  const q = useQuery(queueQuery(filters));
  const settings = useQuery(automationSettingsQuery);
  const items = q.data?.items ?? [];
  const held = q.data?.count ?? 0;
  const groups = [...new Set(items.map(item => item.kind))].map(key => ({ key, rows: items.filter(item => item.kind === key) }));

  async function run(action: () => Promise<string | void>, done?: string) {
    setBusy(true);
    try { toast.success((await action()) || done || "Done"); }
    catch (error) { toast.error(errorMessage(error)); }
    finally { setBusy(false); }
  }

  if (q.isError) return <Page><EmptyState icon={Inbox} title="Unable to load the queue" body={errorMessage(q.error)} action={<Button onClick={() => void q.refetch()}>Try again</Button>} /></Page>;

  return <Page>
    <Stack gap={4}>
      <Inline justify="between" align="start" wrap>
        <Stack gap={1}>
          <Text as="h2" variant="headline">{held} waiting</Text>
          <Text variant="caption" tone="secondary">{settings.data?.autoSendAutomated
            ? "Auto-send is on. New automated messages go out without waiting here."
            : "Nothing here has been sent. Read it, change it if you want to, then release it."}</Text>
        </Stack>
        {items.length > 0 && <Button fullWidth={false} loading={busy} icon={<Send aria-hidden size={18} />} onClick={() => setConfirmingAll(true)}>Release all shown</Button>}
      </Inline>
      <ResponsiveFilters
        title="Filter the queue"
        summary={`${items.length} shown${kind ? ` · ${kindLabel(kind)}` : ""}${channel ? ` · ${channel === "sms" ? "text" : "email"}` : ""}`}
        onReset={() => setSearch({ kind: undefined, channel: undefined, q: undefined })}
        search={<SearchBar value={query} onChange={value => setSearch({ q: value || undefined })} placeholder="Search words, address or person" />}>
        <Picker label="Kind" value={kind} onChange={value => setSearch({ kind: value || undefined })} options={[{ value: "", label: "Every kind" }, ...Object.entries(KIND_LABELS).map(([value, label]) => ({ value, label }))]} />
        <Picker label="Channel" value={channel} onChange={value => setSearch({ channel: (value || undefined) as "sms" | "email" | undefined })} options={[{ value: "", label: "Email and text" }, { value: "email", label: "Email" }, { value: "sms", label: "Text" }]} />
      </ResponsiveFilters>

      {!q.data ? <Skeleton variant="cards" count={3} />
        : items.length === 0
          ? <EmptyState icon={Inbox} title={query || kind || channel ? "Nothing matches those filters" : "The queue is empty"}
              body={query || kind || channel ? "Clear a filter to see the rest." : "Automated messages appear here for you to read before they go to anybody."} />
          // One column of kinds, each kind's messages filling the width in a
          // two-column grid on a laptop. Putting the kinds side by side instead
          // left one column empty whenever the venue had six of one and one of
          // another, which is the normal shape of a queue.
          : <Stack gap={4}>
            {groups.map(group => <Stack key={group.key} gap={2}>
              <Inline justify="between"><Text as="h2" variant="title">{kindLabel(group.key)}</Text><StatusChip tone="neutral">{group.rows.length}</StatusChip></Inline>
              <Workspace variant="overview">
                {group.rows.map(item => <Region key={item.id} area="main">
                  <QueueItemCard item={item} busy={busy}
                    onEdit={() => setEditing(item)}
                    onRelease={() => void run(async () => {
                      const result = await queueApi.release([item.id]);
                      return releaseOutcome(result);
                    })}
                    onCancel={() => setCancelling(item)} />
                </Region>)}
              </Workspace>
            </Stack>)}
          </Stack>}
    </Stack>

    {editing && <EditSheet key={editing.id} item={editing} onClose={() => setEditing(null)} />}

    <BottomSheet open={Boolean(cancelling)} onClose={() => setCancelling(null)} title="Cancel this message?"
      footer={<Button loading={busy} onClick={async () => {
        if (cancelling) await run(async () => { await queueApi.cancel([cancelling.id]); }, "Message cancelled");
        setCancelling(null);
      }}>Cancel it</Button>}>
      <Stack gap={2}>
        <Text>The message to {cancelling ? who(cancelling) : "this client"} will not be sent.</Text>
        <Text tone="secondary">It stays on the record, so what the automation proposed and what you decided are both visible later.</Text>
      </Stack>
    </BottomSheet>

    <BottomSheet open={confirmingAll} onClose={() => setConfirmingAll(false)} title="Release all shown"
      footer={<Button loading={busy} onClick={async () => {
        await run(async () => {
          const result = await queueApi.releaseAll(filters);
          return releaseOutcome(result);
        });
        setConfirmingAll(false);
      }}>Send them</Button>}>
      <Stack gap={3}>
        <Text>This will send {items.length} {items.length === 1 ? "message" : "messages"} to clients — the {items.length === 1 ? "one" : "ones"} listed behind this sheet.</Text>
        {/* The count the button acts on and the count the badge shows are
            different numbers whenever a filter is on, so say both. */}
        {held > items.length && <Text tone="secondary">The other {held - items.length} waiting {held - items.length === 1 ? "message stays" : "messages stay"} in the queue.</Text>}
        <Text tone="secondary">Review each one until you trust the automation. Anything you are not sure about can be cancelled instead.</Text>
      </Stack>
    </BottomSheet>
  </Page>;

  /** One queued message. A card rather than a row with quick actions: three
   * buttons beside a person's name and a preview does not fit on a phone, and
   * this is a screen somebody works through one item at a time. */
  function QueueItemCard({ item, busy, onEdit, onRelease, onCancel }: { item: QueueItem; busy: boolean; onEdit: () => void; onRelease: () => void; onCancel: () => void }) {
    const when = item.scheduledFor ? `Waiting until ${fmtStamp(item.scheduledFor)}` : "Ready to send";
    // A message about nobody in particular (a company-wide note) has no record
    // to open, so the card simply does not offer one.
    const open = item.bookingId
      ? () => navigate({ to: "/bookings/$id", params: { id: item.bookingId! } })
      : item.leadId ? () => navigate({ to: "/leads/$id", params: { id: item.leadId! } }) : undefined;
    return <Card><Stack gap={3}>
      {/* The message gets the width. A `Row` would put the status chips in a
          trailing column and squeeze the words into a third of the card, which
          is the opposite of what a screen for reading messages should do. */}
      <Inline gap={3} align="start">
        <span aria-hidden>{item.channel === "sms" ? <MessageSquare size={22} /> : <Mail size={22} />}</span>
        <Stack gap={1}>
          <Text variant="body">{who(item)}{item.subject ? ` — ${item.subject}` : ""}</Text>
          <Text variant="callout" tone="secondary">{item.body.slice(0, 220)}{item.body.length > 220 ? "…" : ""}</Text>
        </Stack>
      </Inline>
      <Inline gap={2} wrap>
        <StatusChip tone={item.scheduledFor ? "warn" : "neutral"}>{when}</StatusChip>
        <StatusChip tone="neutral">{item.channel === "sms" ? "Text" : "Email"}</StatusChip>
        {item.venueName && <StatusChip tone="neutral">{item.venueName}</StatusChip>}
        {open && <Button variant="secondary" fullWidth={false} onClick={open}>Open the record</Button>}
      </Inline>
      {/* Named for the person each one acts on: a screen reader hearing "Edit,
          Release, Cancel" eighteen times cannot tell the rows apart. */}
      <Inline gap={2} wrap>
        {/* An agreement invitation carries the signer's private link, which the
            queue redacts before it reaches this screen. Saving what is shown here
            would store the redaction over the real link, so the service refuses
            it — and a button that always fails is worse than no button. */}
        {!item.contractId && <Button variant="secondary" fullWidth={false} disabled={busy} onClick={onEdit} aria-label={`Edit the message to ${who(item)}`}>Edit</Button>}
        <Button fullWidth={false} disabled={busy} onClick={onRelease} aria-label={`Send the message to ${who(item)}`}>Release</Button>
        <Button variant="secondary" fullWidth={false} disabled={busy} onClick={onCancel} aria-label={`Cancel the message to ${who(item)}`}>Cancel</Button>
      </Inline>
    </Stack></Card>;
  }
}

/** Editing before releasing. The words are the venue's, not the template's. */
/** Mounted per message (`key`), so closing it really does discard the draft
 * rather than keeping an abandoned one to present as the message next time. */
function EditSheet({ item, onClose }: { item: QueueItem; onClose: () => void }) {
  const toast = useToast();
  const [body, setBody] = useState(item.body);
  const [subject, setSubject] = useState(item.subject ?? "");
  const [busy, setBusy] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const dirty = body !== item.body || subject !== (item.subject ?? "");

  async function save() {
    if (!dirty) return;
    setBusy(true);
    try {
      await queueApi.edit(item.id, { body, ...(item.channel === "email" && subject ? { subject } : {}) });
      toast.success("Message saved");
      onClose();
    } catch (error) { toast.error(errorMessage(error)); }
    finally { setBusy(false); }
  }

  return <>
    <UnsavedChanges dirty={dirty} pending={busy} />
    {/* Closing a sheet is the phone's back gesture, and the router guard cannot
        see it — so a half-written message asks before it is thrown away. */}
    <BottomSheet open onClose={() => (dirty ? setConfirmingClose(true) : onClose())} title="Edit before sending"
      footer={<Button loading={busy} disabled={!dirty} onClick={() => void save()}>Save changes</Button>}>
      <Stack gap={3}>
        <Card><Stack gap={1}>
          <Text variant="caption" tone="secondary">To</Text>
          <Text>{item.contactName ?? item.toAddr ?? "Unknown recipient"}</Text>
        </Stack></Card>
        {item.channel === "email" && <TextField label="Subject" value={subject} onChange={event => setSubject(event.target.value)} />}
        <TextArea label={item.channel === "sms" ? "Text message" : "Email body"} rows={10} value={body} maxLength={item.channel === "sms" ? 1600 : undefined}
          hint={item.channel === "sms" ? `${body.length} of 1600 characters` : undefined}
          onChange={event => setBody(event.target.value)} />
        {item.channel === "email" && <Text variant="caption" tone="secondary">Emails keep the branded layout. Your changes replace the words inside it.</Text>}
      </Stack>
    </BottomSheet>
    <BottomSheet open={confirmingClose} onClose={() => setConfirmingClose(false)} title="Keep your changes?"
      footer={<Button onClick={() => setConfirmingClose(false)}>Keep editing</Button>}>
      <Stack gap={3}>
        <Text>You have changed this message but not saved it.</Text>
        <Button variant="secondary" onClick={() => { setConfirmingClose(false); onClose(); }}>Discard my changes</Button>
      </Stack>
    </BottomSheet>
  </>;
}
